import { defaults } from "lodash"
import { ErrorResult } from "models/dto"
import {
  awaitMilliseconds,
  buildLoginUrl,
  getHtmlLanguage,
  random,
} from "utils/helpers"
import { asAspnetErrorResponse } from "models/aspnet-core-models"

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Base functionality
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface RequestHeaders {
  [x: string]: string
}

interface RequestOptions {
  contentType: string
  accept: string
  serializeBody: boolean
  parseResponse: boolean
  customHeaders: RequestHeaders
  redirectOn401: boolean
  is404Expected: boolean
  is429TooManyRequestsExpected: boolean
  /**
   * Send the request again (three attempts in all) when no response arrives,
   * or when the server refuses it with 429. Only for a request the server may
   * get twice: with no response there is no telling whether the first one
   * arrived. Most endpoints are not such: a repeated create makes two.
   */
  retryWhenNoResponse: boolean
  onProgress?: (percent: number) => void
  onAborted?: () => void
}

export interface RawRequestResult {
  statusCode: number
  body: any | null
}

export function serverGet(url: string, opts?: Partial<RequestOptions>) {
  return serverRequest("GET", url, null, opts)
}

export function serverPut(
  url: string,
  body: any,
  opts?: Partial<RequestOptions>
) {
  return serverRequest("PUT", url, body, opts)
}

export function serverPost(
  url: string,
  body: any,
  opts?: Partial<RequestOptions>
) {
  return serverRequest("POST", url, body, opts)
}

export function serverPatch(
  url: string,
  body: any,
  opts?: Partial<RequestOptions>
) {
  return serverRequest("PATCH", url, body, opts)
}

export function serverDelete(url: string, opts?: Partial<RequestOptions>) {
  return serverRequest("DELETE", url, null, opts)
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Events
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type ServerEventListener = (event: ServerEvent) => void
export type ServerMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH"

export interface ServerEvent {
  type:
    | "SERVER_REQUEST_STARTED"
    | "SERVER_REQUEST_ENDED"
    | "SERVER_REQUEST_ERROR"
    | "CONNECTION_LOST"
  url: string
  method: ServerMethod
}

export interface ServerRequestStartedEvent extends ServerEvent {
  type: "SERVER_REQUEST_STARTED"
}

export interface ServerRequestEndedEvent extends ServerEvent {
  type: "SERVER_REQUEST_ENDED"
  statusCode: number
}

export interface ServerRequestErrorEvent extends ServerEvent {
  type: "SERVER_REQUEST_ERROR"
  error: ErrorResult
}

export interface ServerConnectionLostEvent extends ServerEvent {
  type: "CONNECTION_LOST"
}

/**
 * What is known about a request that failed. It rides on the rejected error
 * (which is whatever the server answered, so its shape varies) as a hidden
 * property: read it with getFailedRequestInfo.
 */
export interface FailedRequestInfo {
  method: ServerMethod
  url: string
  /** 0 when no response arrived: the network, not the server */
  statusCode: number
  /** the response's X-Request-Id: the id the server logged the request under */
  requestId: string | null
}

const FAILED_REQUEST_INFO = "__failedRequestInfo"

function attachFailedRequestInfo(error: unknown, info: FailedRequestInfo) {
  if (typeof error !== "object" || error === null) return
  try {
    // not enumerable: the error is also serialized and shown by callers
    Object.defineProperty(error, FAILED_REQUEST_INFO, {
      value: info,
      enumerable: false,
      configurable: true,
    })
  } catch (e) {}
}

export function getFailedRequestInfo(error: unknown): FailedRequestInfo | null {
  if (typeof error !== "object" || error === null) return null
  const info = (error as any)[FAILED_REQUEST_INFO]
  return typeof info === "object" && info !== null ? info : null
}

const serverEventListeners: ServerEventListener[] = []

export function serverAddEventListener(listener: ServerEventListener) {
  serverEventListeners.push(listener)
}

export function serverRemoveEventListener(listener: ServerEventListener) {
  const idx = serverEventListeners.indexOf(listener)
  if (idx != -1) {
    serverEventListeners.splice(idx, 1)
    return true
  } else {
    return false
  }
}

function broadcastRequestStarted(url: string, method: ServerMethod) {
  const event: ServerRequestStartedEvent = {
    type: "SERVER_REQUEST_STARTED",
    url,
    method,
  }
  serverEventListeners.forEach(function (listener) {
    try {
      listener(event)
    } catch (e) {
      console.log("Failed to notify", event.type, "error:\n", e)
    }
  })
}

function broadcastRequestEnded(
  url: string,
  method: ServerMethod,
  statusCode: number
) {
  const event: ServerRequestEndedEvent = {
    type: "SERVER_REQUEST_ENDED",
    url,
    method,
    statusCode,
  }
  serverEventListeners.forEach(function (listener) {
    try {
      listener(event)
    } catch (e) {
      console.log("Failed to notify", event.type, "error:\n", e)
    }
  })
}

function broadcastConnectionLost(url: string, method: ServerMethod) {
  const event: ServerConnectionLostEvent = {
    type: "CONNECTION_LOST",
    url,
    method,
  }
  serverEventListeners.forEach(function (listener) {
    try {
      listener(event)
    } catch (e) {
      console.log("Failed to notify", event.type, "error:\n", e)
    }
  })
}

function broadcastRequestError(
  url: string,
  method: ServerMethod,
  error: ErrorResult
) {
  const event: ServerRequestErrorEvent = {
    type: "SERVER_REQUEST_ERROR",
    url,
    method,
    error,
  }
  serverEventListeners.forEach(function (listener) {
    try {
      listener(event)
    } catch (e) {
      console.log("Failed to notify", event.type, "error:\n", e)
    }
  })
}

export async function serverRequest(
  method: ServerMethod,
  url: string,
  body?: any | null,
  opts?: Partial<RequestOptions>
): Promise<RawRequestResult> {
  let options: RequestOptions = defaults(opts || ({} as any), {
    serializeBody: !!body,
    parseResponse: true,
    contentType: "application/json",
    accept: "application/json",
    customHeaders: {},
    redirectOn401: true,
    is429TooManyRequestsExpected: false,
    retryWhenNoResponse: false,
  })

  try {
    const lang = getHtmlLanguage()
    if (!url.startsWith(`/${lang}/`)) {
      if (!url.startsWith("/")) {
        url = "/" + lang + "/" + url
      } else {
        url = "/" + lang + url
      }
    }
  } catch (e) {}

  const MAX_ATTEMPTS = options.retryWhenNoResponse ? 3 : 1
  let waitTime = random(800, 1000)

  async function waitBeforeRetry() {
    await awaitMilliseconds(waitTime)
    waitTime += random(200, 300)
  }

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      return await sendHttpRequest(method, url, options, body)
    } catch (e) {
      // Only a request the server did not act on is sent again, and only
      // when the caller said it may be: one that got no response (status 0:
      // the network dropped it), or one refused with 429 because another
      // request of this player was being served. Anything else the server
      // answered is final.
      const statusCode = getFailedRequestInfo(e)?.statusCode
      if (
        !options.retryWhenNoResponse ||
        (statusCode !== 0 && statusCode !== 429)
      ) {
        throw e
      }

      if (attempt < MAX_ATTEMPTS - 1) {
        await waitBeforeRetry()
      } else if (statusCode === 429) {
        // still refused: the connection is fine, so not "connection lost"
        throw e
      }
    }
  }

  // if we get here, this is an error
  console.warn("failed_after_attempts", method, url, body)
  broadcastConnectionLost(url, method)
  const error: ErrorResult = {
    statusCode: 0,
    message: "failed_after_attempts",
    body: null,
  }
  attachFailedRequestInfo(error, {
    method,
    url,
    statusCode: 0,
    requestId: null,
  })
  throw error
}

function sendHttpRequest(
  method: ServerMethod,
  url: string,
  options: RequestOptions,
  body?: any | null
): Promise<RawRequestResult> {
  return new Promise<RawRequestResult>((resolve, reject) => {
    const req = new XMLHttpRequest()
    req.open(method, url, true)

    const isFileUpload = body instanceof File || body instanceof Blob

    if (isFileUpload) {
      // NOTE: do not set Content-Type when uploading files!
    } else {
      if (!!body) {
        req.setRequestHeader("Content-Type", options.contentType)
      }
    }

    if (options.parseResponse) {
      req.setRequestHeader("Accept", options.accept)
    }

    for (const header in options.customHeaders) {
      if (options.customHeaders.hasOwnProperty(header)) {
        const headerValue = options.customHeaders[header]
        req.setRequestHeader(header, headerValue)
      }
    }

    broadcastRequestStarted(url, method)

    const onProgress = options.onProgress
    if (typeof onProgress === "function" && body instanceof File) {
      const fileSize = body.size
      req.upload.addEventListener("progress", function (e) {
        if (e.loaded <= fileSize) {
          const percent = Math.round((100 * e.loaded) / fileSize)
          onProgress(percent)
        }
        if (e.loaded === fileSize) {
          onProgress(100)
        }
      })
    }

    req.onreadystatechange = () => {
      if (req.readyState === XMLHttpRequest.DONE) {
        if (
          (200 <= req.status && req.status < 300) ||
          (404 == req.status && !!options.is404Expected) ||
          (429 == req.status && !!options.is429TooManyRequestsExpected)
        ) {
          let body: any | null = null

          if (!!req.responseText) {
            if (options.parseResponse) {
              try {
                body = JSON.parse(req.responseText)
              } catch (e) {
                console.warn(e)

                const error: ErrorResult = {
                  statusCode: req.status,
                  message: "cant_parse_response",
                  body: req.responseText,
                }
                attachFailedRequestInfo(error, {
                  method,
                  url,
                  statusCode: req.status,
                  requestId: req.getResponseHeader("X-Request-Id"),
                })
                broadcastRequestError(url, method, error)
                reject(error)
                return
              }
            } else {
              body = req.responseText
            }
          }

          broadcastRequestEnded(url, method, req.status)
          resolve({
            statusCode: req.status,
            body: body,
          })
        } else if (401 === req.status && options.redirectOn401) {
          const redirectUrl = buildLoginUrl()
          location.assign(redirectUrl)
        } else {
          let error: ErrorResult = {
            statusCode: req.status,
            message: req.statusText,
            body: req.response,
          }

          try {
            if (req.responseText && options.parseResponse) {
              error = JSON.parse(req.responseText)

              const aspnetCoreError = asAspnetErrorResponse(error)
              if (aspnetCoreError !== null) {
                console.warn(
                  method,
                  url,
                  "With body",
                  body,
                  "Status:",
                  req.status,
                  "\n",
                  aspnetCoreError
                )
              }
            }
          } catch (e) {
            console.warn(e)
          }

          attachFailedRequestInfo(error, {
            method,
            url,
            statusCode: req.status,
            requestId: req.getResponseHeader("X-Request-Id"),
          })
          broadcastRequestError(url, method, error)
          reject(error)
        }
      } else if (req.readyState === XMLHttpRequest.UNSENT) {
        const onAborted = options.onAborted
        if (typeof onAborted === "function") {
          onAborted()
        }
      }
    }

    if (!!body) {
      if (isFileUpload) {
        const formElement = document.createElement("form")
        formElement.action = method
        formElement.enctype = "multipart/form-data"

        const form = new FormData(formElement)
        form.append("file", body as File)
        req.send(form)
      } else {
        if (options.serializeBody) {
          try {
            const data = JSON.stringify(body)
            req.send(data)
          } catch (e) {
            console.warn(e)

            const error = {
              statusCode: 0,
              message: "cant_serialize",
              body: e,
            }

            broadcastRequestError(url, method, error)
            reject(error)
          }
        } else {
          req.send(body)
        }
      }
    } else {
      req.send()
    }
  })
}
